// SPDX-License-Identifier: AGPL-3.0-or-later

import Accessibility from '@app/features/accessibility/state/Accessibility';
import type {Channel} from '@app/features/channel/models/Channel';
import styles from '@app/features/forum/components/Forum.module.css';
import {ForumPostCard} from '@app/features/forum/components/ForumPostCard';
import * as D from '@app/features/forum/utils/ForumMessageDescriptors';
import {
	buildForumPostRows,
	FORUM_GRID_GAP_PX,
	type ForumPostRow,
	getForumGridColumns,
} from '@app/features/forum/utils/ForumPostList';
import {observeResize} from '@app/features/platform/utils/SharedResizeObserver';
import {useScrollMargin} from '@app/features/ui/hooks/useScrollMargin';
import {getAppRemScale} from '@app/features/ui/utils/AppZoomUtils';
import {useLingui} from '@lingui/react/macro';
import {
	defaultRangeExtractor,
	type Range,
	useVirtualizer,
	type VirtualItem,
	type Virtualizer,
} from '@tanstack/react-virtual';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import {type RefObject, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react';

const LIST_ROW_GAP_PX = 8;
const ESTIMATED_LIST_ROW_HEIGHT_PX = 107;
const ESTIMATED_GRID_CARD_BODY_HEIGHT_PX = 78;
const ESTIMATED_SECTION_ROW_HEIGHT_PX = 28;
const GRID_MEDIA_ASPECT_RATIO = 10 / 16;
const OVERSCAN_ROWS = 4;
const MAX_SAVED_SCROLLS = 16;

// Where a list was when it went away (to a post on the phone layout, to another channel), with the measured rows.
// The offset is into the list, without the scroll margin: the content above the list may have another height.
const savedScrolls = new Map<string, {offset: number; measurements: Array<VirtualItem>}>();

// Sizes come from the virtualizer's one ResizeObserver only: a mounted row is never read for its height.
function measureRow(
	element: HTMLDivElement,
	entry: ResizeObserverEntry | undefined,
	instance: Virtualizer<HTMLDivElement, HTMLDivElement>,
): number {
	const box = entry?.borderBoxSize?.[0];
	if (box) return Math.round(box.blockSize);
	const index = instance.indexFromElement(element);
	return instance.itemSizeCache.get(instance.options.getItemKey(index)) ?? instance.options.estimateSize(index);
}

interface ForumRowAnchor {
	rows: ReadonlyArray<ForumPostRow>;
	posts: ReadonlyArray<{postId: string; start: number}>;
}

interface ForumPostRowsProps {
	forum: Channel;
	current: ReadonlyArray<Channel>;
	archived: ReadonlyArray<Channel>;
	grid: boolean;
	scroller: HTMLDivElement | null;
	contentRef: RefObject<HTMLDivElement | null>;
	scrollKey: string;
}

export const ForumPostRows = observer((props: ForumPostRowsProps) => {
	const {forum, current, archived, grid, scroller, contentRef, scrollKey} = props;
	const {i18n} = useLingui();
	const listRef = useRef<HTMLDivElement>(null);
	const [width, setWidth] = useState(0);
	useLayoutEffect(() => {
		const list = listRef.current;
		if (!list) return;
		setWidth(list.clientWidth);
		return observeResize(list, (entry) => setWidth(entry.contentRect.width));
	}, []);
	const scrollerRef = useMemo(() => ({current: {getViewportElement: () => scroller}}), [scroller]);
	const scrollMargin = useScrollMargin(listRef, contentRef, scrollerRef);
	const remScale = getAppRemScale();
	const columns = grid ? getForumGridColumns(width, remScale) : 1;
	const gap = (grid ? FORUM_GRID_GAP_PX : LIST_ROW_GAP_PX) * remScale;
	const rows = useMemo(() => buildForumPostRows(current, archived, columns), [current, archived, columns]);
	const sizing = `${Accessibility.zoomLevel}:${grid}:${columns}`;
	const getItemKey = useCallback((index: number) => `${sizing}:${rows[index]?.key ?? index}`, [sizing, rows]);
	const estimateSize = (index: number) => {
		if (rows[index]?.type === 'older') return ESTIMATED_SECTION_ROW_HEIGHT_PX * remScale;
		if (!grid) return ESTIMATED_LIST_ROW_HEIGHT_PX * remScale;
		const cardWidth = (width - gap * (columns - 1)) / columns;
		return cardWidth * GRID_MEDIA_ASPECT_RATIO + ESTIMATED_GRID_CARD_BODY_HEIGHT_PX * remScale;
	};
	const rowByPost = useMemo(
		() =>
			new Map(
				rows.flatMap((row, index) => (row.type === 'posts' ? row.posts.map((post) => [post.id, index] as const) : [])),
			),
		[rows],
	);
	// A focused card stays mounted when the list scrolls away from it, or focus would fall out to the page.
	const rangeExtractor = useCallback(
		(range: Range) => {
			const indexes = defaultRangeExtractor(range);
			const active = listRef.current?.ownerDocument.activeElement;
			const focused = rowByPost.get(active?.closest<HTMLElement>('[data-post-id]')?.dataset.postId ?? '');
			return focused === undefined || indexes.includes(focused) ? indexes : [...indexes, focused].sort((a, b) => a - b);
		},
		[rowByPost],
	);
	const [saved] = useState(() => savedScrolls.get(scrollKey));
	const virtualizer = useVirtualizer<HTMLDivElement, HTMLDivElement>({
		count: rows.length,
		getScrollElement: () => scroller,
		estimateSize,
		getItemKey,
		gap,
		overscan: OVERSCAN_ROWS,
		scrollMargin,
		measureElement: measureRow,
		rangeExtractor,
		initialOffset: saved ? () => saved.offset + scrollMargin : undefined,
		initialMeasurementsCache: saved?.measurements,
	});
	const scrollKeyRef = useRef(scrollKey);
	scrollKeyRef.current = scrollKey;
	useEffect(
		() => () => {
			savedScrolls.delete(scrollKeyRef.current);
			savedScrolls.set(scrollKeyRef.current, {
				offset: (virtualizer.scrollOffset ?? 0) - virtualizer.options.scrollMargin,
				measurements: virtualizer.takeSnapshot(),
			});
			const oldest = savedScrolls.keys().next().value;
			if (savedScrolls.size > MAX_SAVED_SCROLLS && oldest !== undefined) savedScrolls.delete(oldest);
		},
		[virtualizer],
	);
	// With the real margin: the list sits below the search card, and the first renders do not know that yet.
	const restoreOffsetRef = useRef(saved?.offset ?? null);
	const anchorRef = useRef<ForumRowAnchor | null>(null);
	useLayoutEffect(() => {
		const offset = restoreOffsetRef.current;
		if (offset !== null) {
			if (!scroller || scrollMargin === 0) return;
			restoreOffsetRef.current = null;
			scroller.scrollTop = offset + scrollMargin;
			return;
		}
		// overflow-anchor is off for the virtualizer, so a reorder is held here. The median follows the cards that stayed
		// together: a visible post that moved to the top is one outlier and does not take the view with it.
		const anchor = anchorRef.current;
		if (anchor && anchor.rows !== rows && scroller) {
			const shifts = anchor.posts.flatMap(({postId, start}) => {
				const item = virtualizer.measurementsCache[rowByPost.get(postId) ?? -1];
				return item ? [item.start - start] : [];
			});
			const shift = shifts.sort((a, b) => a - b)[shifts.length >> 1] ?? 0;
			if (shift !== 0) scroller.scrollTop += shift;
		}
		const viewTop = virtualizer.scrollOffset ?? 0;
		const viewBottom = viewTop + (virtualizer.scrollRect?.height ?? 0);
		const posts = virtualizer.getVirtualItems().flatMap((item) => {
			const row = rows[item.index];
			return row?.type === 'posts' && item.end > viewTop && item.start < viewBottom
				? [{postId: row.posts[0]!.id, start: item.start}]
				: [];
		});
		anchorRef.current = viewTop > scrollMargin ? {rows, posts} : null;
	});
	const renderRow = (row: ForumPostRow) =>
		row.type === 'older' ? (
			<div className={styles.sectionTitle} data-flx="forum.forum-post-rows.section-title">
				{i18n._(D.OLDER_POSTS_DESCRIPTOR)}
			</div>
		) : (
			row.posts.map((post) => (
				<ForumPostCard
					key={post.id}
					forum={forum}
					post={post}
					grid={grid}
					data-flx="forum.forum-post-rows.forum-post-card"
				/>
			))
		);
	return (
		<div
			ref={listRef}
			className={clsx(styles.postRows, grid && styles.postRowsGrid)}
			style={{height: virtualizer.getTotalSize()}}
			data-flx="forum.forum-post-rows.post-rows"
		>
			{virtualizer.getVirtualItems().map((virtualRow) => {
				const row = rows[virtualRow.index];
				if (!row) return null;
				return (
					<div
						key={row.key}
						ref={virtualizer.measureElement}
						data-index={virtualRow.index}
						className={clsx(styles.postRow, row.type === 'older' ? styles.sectionRow : grid && styles.postGrid)}
						style={{transform: `translateY(${virtualRow.start - scrollMargin}px)`}}
						data-flx="forum.forum-post-rows.row"
					>
						{renderRow(row)}
					</div>
				);
			})}
		</div>
	);
});
